import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  TemplateRef,
  ViewChild,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputGroupComponent, NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import {
  debounceTime,
  finalize,
  Observable,
  of,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { DrawerHeaderComponent } from '../../../../../components/drawer-header/drawer-header.component';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { LoaderService } from '../../../../../core/services/loader.service';
import { CommonService } from '../../../../../shared/services/common.service';
import { AuditType } from '../../../../bip-template/bip-template.enums';
import { InsightMode } from '../../../insights.enums';
import {
  CaseDetailModel,
  EditCaseDrawerParams,
} from '../../../insights.interface';
import { DataLogDetailComponent } from './data-log-detail/data-log-detail.component';
import { DataLogFilterComponent } from './data-log-filter/data-log-filter.component';
import {
  DataLogFilterDrawerParams,
  DataLogs,
  DataLogsQueryParams,
} from './data-log.interface';
import { DataLogService } from './data-log.service';
import { UserDatePipe } from '../../../../../shared/pipes/user-date.pipe';

@Component({
  selector: 'app-data-log',
  standalone: true,
  imports: [
    NzFormModule,
    NzInputGroupComponent,
    NzInputModule,
    NzIconModule,
    NzButtonModule,
    SvgIconComponent,
    NzTableModule,
    CommonModule,
    DrawerHeaderComponent,
    ReactiveFormsModule,
    NzSpinModule,
    UserDatePipe,
  ],
  templateUrl: './data-log.component.html',
  styleUrl: './data-log.component.scss',
})
export class DataLogComponent implements OnInit, OnDestroy {
  private readonly dataLogService = inject(DataLogService);

  @ViewChild('drawerLogDetail')
  drawerLogDetail!: TemplateRef<void>;
  pageSize = 10;
  pageIndex = 1;
  totalRow = 0;
  sort = '';
  searchTitle = new FormControl('');
  sortOrder: number | undefined = 1;
  dataLogs$ = this.dataLogService.dataLogs$;
  private readonly destroy$ = new Subject<void>();
  loading$: Observable<boolean> = of(false);
  appliedFilters: DataLogFilterDrawerParams['appliedFilters'] | null = null;
  appliedFilterLength = 0;
  AuditTypeEnums = AuditType;
  orgId = '';
  caseParams: EditCaseDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.VIEW,
  };

  drawerParams = {
    templateId: null,
    dataAuditTypeId: null,
    userId: null,
    dateFilter: null,
  };
  listOfColumn = [
    {
      title: 'User ',
      compare: null,
      key: 'updatedBy',
    },
    {
      title: 'Behavior Category ',
      compare: null,
      key: 'behaviorCategoryName',
    },
    {
      title: 'Date of Entry',
      compare: null,
      key: 'dataDate',
    },
    {
      title: 'Data Type',
      compare: null,
      key: 'dataUnitType',
    },
    {
      title: 'Action',
      compare: null,
      key: '',
    },
    {
      title: 'Timestamp',
      compare: null,
      key: 'lastModified',
    },
    {
      title: '',
    },
  ];
  constructor(
    private readonly drawerService: DrawerService,
    private readonly commonService: CommonService,
    private readonly loaderService: LoaderService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerSubscription();
    this.drawerService
      .getPersistentState<DataLogFilterDrawerParams>(
        DRAWER_IDS.DATA_LOGS_FILTER
      )
      .subscribe(state => {
        if (state) {
          this.appliedFilters = state.appliedFilters;
          this.appliedFilterLength = state.appliedFilterLength ?? 0;
          this.cdr.detectChanges();
        }
      });
  }

  ngOnInit(): void {
    this.searchTitle.valueChanges
      .pipe(
        debounceTime(1000) // 500ms delay
      )
      .subscribe(() => {
        this.pageIndex = 1;
        this.loadData();
      });
  }

  setupDrawerSubscription() {
    this.drawerService
      .getParams<EditCaseDrawerParams>(DRAWER_IDS.DATA_LOG)
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: EditCaseDrawerParams | null) => {
        if (!params) return;
        this.caseParams = params;
      });
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    this.pageSize = params.pageSize;
    this.pageIndex = params.pageIndex;
    const currentSort = params.sort.find(item => item.value !== null);
    this.sort = currentSort?.key || '';
    const sortDirection = currentSort?.value === 'ascend' ? 0 : 1;
    this.sortOrder =
      !!currentSort && currentSort.value ? sortDirection : undefined;
    this.loadData();
  }

  onChange() {
    this.pageIndex = 1;
    this.loadData();
  }

  loadData(): void {
    const queryParams: DataLogsQueryParams = {
      pageSize: this.pageSize,
      page: this.pageIndex,
      sort: this.sort ?? undefined,
      sortOrder: this.sortOrder,
      caseId: this.caseParams?.case?.id || null,
      searchTerm: this.searchTitle.value || null,
      dataAuditTypeId: this.appliedFilters?.dataAuditTypeId || null,
      dataUnitType: this.appliedFilters?.dataUnitType || null,
      startDataDateRange: this.appliedFilters?.startDataDateRange || null,
      endDataDateRange: this.appliedFilters?.endDataDateRange || null,
      startDateRange: this.appliedFilters?.startDateRange || null,
      endDateRange: this.appliedFilters?.endDateRange || null,
      userId: this.appliedFilters?.userId || null,
    };
    this.loaderService.show();
    this.dataLogService
      .getDataLogs(queryParams)
      .pipe(
        take(1), // Take only one response
        finalize(() => this.loaderService.hide()), // End loading after response
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          this.totalRow = response.result.totalRows;
          this.loaderService.hide();
        },
        error: (error: ErrorResponse) => {
          this.loaderService.hide();
          this.commonService.notify('error', error.message);
        },
      });
  }

  openFilterDrawer(): void {
    this.drawerService
      .openDrawer(
        DataLogFilterComponent,
        'Filter',
        780,
        true,
        undefined,
        {
          appliedFilterLength: this.appliedFilterLength,
          appliedFilters: this.appliedFilters,
          caseId: this.caseParams?.case?.id || 0,
        },
        DRAWER_IDS.DATA_LOGS_FILTER
      )
      .afterClose.subscribe(res => {
        if (res) {
          this.pageIndex = 1;
          this.loadData();
        }
      });
  }

  clearFilters() {
    this.appliedFilters = null;
    this.appliedFilterLength = 0;
    this.pageIndex = 1;
    this.drawerService.clearPersistentState(DRAWER_IDS.DATA_LOGS_FILTER);
    this.loadData();
  }

  openTemplateLogDetailsDrawer(data: DataLogs): void {
    this.drawerService.openDrawer(
      DataLogDetailComponent,
      this.drawerLogDetail as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        selectedLog: data,
      },
      DRAWER_IDS.DATA_LOG_DETAIL
    );
  }

  closeLogDetailDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.DATA_LOG_DETAIL);
  }

  ngOnDestroy(): void {
    this.drawerService.clearPersistentState(DRAWER_IDS.DATA_LOGS_FILTER);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
